import React, { useMemo } from 'react';
import type { TimeRange } from '../../types/dashboard';
import { fmtMoney } from '../../utils/formatters';
import { getTradePnl, normalizeTradeStatus } from '../../lib/trades/authoritativeTradeData';
import { filterTradesForDashboardRange } from '../../lib/trades/dashboardBucketAnalytics';

interface LargestGainWidgetProps {
  dailyData?: any[];
  trades?: any[];
  timeRange?: TimeRange;
}

export const LargestGainWidget: React.FC<LargestGainWidgetProps> = ({ trades = [], timeRange = 'month' }) => {
  const { hasClosedTrades, largestGain } = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) {
      return { hasClosedTrades: false, largestGain: 0 };
    }
    const activeTrades = filterTradesForDashboardRange(trades as Record<string, any>[], timeRange as any);
    const closedPnls = activeTrades
      .filter((t) => normalizeTradeStatus(t) !== 'open')
      .map((t) => getTradePnl(t))
      .filter((pnl) => Number.isFinite(pnl));

    if (closedPnls.length === 0) {
      return { hasClosedTrades: false, largestGain: 0 };
    }

    const gains = closedPnls.filter((pnl) => pnl > 0);
    return {
      hasClosedTrades: true,
      largestGain: gains.length > 0 ? Math.max(...gains) : 0,
    };
  }, [trades, timeRange]);

  return (
    <div className="h-full w-full flex flex-col justify-center items-center p-4">
      {hasClosedTrades ? (
        <div className="w-full text-center" data-testid="largest-gain-amount">
          <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-emerald-600">{fmtMoney(largestGain)}</span>
        </div>
      ) : (
        <div className="text-sm text-zinc-500 dark:text-zinc-400">Import Trades to see the widget</div>
      )}
    </div>
  );
};